import {
  CampaignTypeEnumm,
  RegistrationCampaignSubTypeEnum,
  StockImageAspectRatio,
} from '@sml/utility';

export const STOCK_IMAGE_PICKER_STRINGS = {
  TITLE: 'Photo Library',
  SEARCH_PLACEHOLDER: 'Search photos, e.g. Farmers Market',
  SEARCH_BUTTON: 'Search',
  SUGGESTED_LABEL: 'Try one of these:',
  EMPTY_TITLE: 'No photos found',
  EMPTY_MESSAGE: 'Try a different keyword, or one of the suggestions above.',
  UNAVAILABLE_TITLE: 'Photo library unavailable',
  START_TITLE: 'Search for a photo',
  START_MESSAGE:
    'Type a keyword above to browse professional photos you can use for free.',
  LOAD_MORE: 'Load more photos',
  CANCEL: 'Cancel',
  ADD_SINGLE: 'Add photo',
  ADD_MULTIPLE: 'Add {count} photos',
  PREVIEW: 'Preview',
  CLOSE_PREVIEW: 'Close preview',
  PHOTO_BY: 'Photo by',
  ON_PROVIDER: 'on',
  PHOTOS_PROVIDED_BY: 'Photos provided by',
  ADDING: 'Adding photos...',
  SINGLE_SELECT_HINT: 'Select one photo',
  MULTI_SELECT_HINT: 'Select one or more photos',
  SELECTED_SUFFIX: 'selected',
  CLEAR_SELECTION: 'Clear selection',
  PREVIEW_ADD: 'Add photo',
  PREVIEW_ADDED: 'Added',
};

export type StockImagePickerContext = 'general' | 'campaign' | 'initiative';

export const STOCK_IMAGE_SUGGESTED_KEYWORDS = [
  'Christmas',
  'Farmers Market',
  'Education',
  'Parade',
  'Volunteers',
  'Concert',
];

export const STOCK_IMAGE_INITIATIVE_KEYWORDS = [
  'Summer Festival',
  'Fall Festival',
  'Christmas Festival',
  'Main Street Shops',
  'Parks',
  'Veterans',
  'Town Fair',
  'Education',
];

export const STOCK_IMAGE_CAMPAIGN_TYPE_KEYWORDS: Record<number, string[]> = {
  [CampaignTypeEnumm['Volunteer Activity']]: [
    'Clean-Up Project',
    'Beautification Volunteers',
    'Festival Volunteers',
    'Program Volunteers',
    'Planting Volunteers',
    'Youth Volunteers',
    'Volunteer',
    'Town Fair',
  ],
  [CampaignTypeEnumm['Sponsorship']]: [
    'Fall Festival',
    'Christmas Festival',
    'Main Street Shops',
    'Summer Festival',
    'Town Fair',
    'Farmers Market',
    'Community Fair',
    'Youth Sports',
    'Golf Tournament',
    'Conferences',
  ],
  [CampaignTypeEnumm['Fundraiser']]: [
    'Veterans Memorial',
    'Playground',
    'Community Activities',
    'Fundraising Dinner',
    'Public Art',
    'Library',
    'Building Restoration',
    'Main Street',
    'Parks Beautification',
    'Historic Building Restoration',
  ],
  [CampaignTypeEnumm['Memorial Program']]: [
    'Engraved Brick',
    'Dedicated Bench',
    'Memorial Tree',
    'Plaque',
    'Parks',
    'American Flags',
  ],
  [CampaignTypeEnumm['Ticketing']]: [
    'Main Street Shopping',
    'Community Concert',
    'Fundraising Dinner',
    'Town Fair',
    'Community',
    'Education Workshop',
    'Kids Class',
  ],
  [CampaignTypeEnumm['Interactive Tour']]: [
    'Historical Homes',
    'Public Art + Statues',
    'Restaurants',
    'Main Streets',
    'Boutique Shops',
    'Military Sites',
    'Parks',
    'Walking Trail',
  ],
  [RegistrationCampaignSubTypeEnum['Vendor Registration']]: [
    'Farmers Market',
    'Food Trucks',
    'Festival Vendors',
    'Craft Show',
    'Holiday Market',
    'Vendor Booths',
    'Main Street Shops',
    'Town Fair',
  ],
  [RegistrationCampaignSubTypeEnum['Community Sign-Up']]: [
    'Hometown Parade',
    'Christmas',
    'Halloween',
    'Spring Activities',
    'Summer Activities',
    '5k Race',
    'Car Show',
    'Summer Camp',
  ],
};

export const STOCK_IMAGE_DEFAULT_QUERY = 'Community Event';

export const STOCK_IMAGE_DEFAULT_QUERY_BY_CAMPAIGN_TYPE: Record<number, string> =
  {
    [CampaignTypeEnumm['Volunteer Activity']]: 'Volunteers',
    [CampaignTypeEnumm['Sponsorship']]: 'Town Fair',
    [CampaignTypeEnumm['Fundraiser']]: 'Small Town',
    [CampaignTypeEnumm['Memorial Program']]: 'Bench with Plaque',
    [CampaignTypeEnumm['Ticketing']]: 'Main Street Shopping',
    [CampaignTypeEnumm['Interactive Tour']]: 'Public Art',
    [RegistrationCampaignSubTypeEnum['Vendor Registration']]: 'Vendor Booths',
    [RegistrationCampaignSubTypeEnum['Community Sign-Up']]: 'Hometown Parade',
  };

export const STOCK_IMAGE_INITIATIVE_DEFAULT_QUERY = 'Town Fair';

export function resolveStockImageDefaultQuery(
  context: StockImagePickerContext,
  campaignTypeId: number | null,
): string {
  if (context === 'initiative') return STOCK_IMAGE_INITIATIVE_DEFAULT_QUERY;
  if (context !== 'campaign' || !campaignTypeId) {
    return STOCK_IMAGE_DEFAULT_QUERY;
  }
  return (
    STOCK_IMAGE_DEFAULT_QUERY_BY_CAMPAIGN_TYPE[campaignTypeId] ??
    STOCK_IMAGE_DEFAULT_QUERY
  );
}

export function resolveStockImageKeywords(
  context: StockImagePickerContext,
  campaignTypeId: number | null,
): string[] {
  if (context === 'initiative') return STOCK_IMAGE_INITIATIVE_KEYWORDS;

  const forType = campaignTypeId
    ? STOCK_IMAGE_CAMPAIGN_TYPE_KEYWORDS[campaignTypeId]
    : undefined;

  return forType ?? STOCK_IMAGE_SUGGESTED_KEYWORDS;
}

/**
 * Shape of a result tile per target ratio, so the grid previews the crop the
 * photo will be saved at. 'original' keeps the old fixed-height tile, since
 * there is no single ratio to show.
 */
export const STOCK_IMAGE_TILE_SHAPE_CLASSES: Record<
  StockImageAspectRatio,
  string
> = {
  square: 'tw-aspect-square',
  // Matches the 700x400 campaign gallery carousel stage.
  landscape: 'tw-aspect-[6/4]',
  // Matches the 3:1 cover banner on the overview page. The tile is shaped by
  // CSS over the provider's own thumbnail rather than by a server-side crop -
  // asking the CDN for a 3:1 derivative of every result got us 503s.
  cover: 'tw-aspect-[3/1]',
  original: 'tw-h-[130px] tablet:tw-h-[150px]',
};

/**
 * Column count per target ratio. A cover tile is a third as tall as it is
 * wide, so the usual four columns leave a ~70px strip you cannot judge a photo
 * from - it gets fewer, wider tiles instead.
 */
export const STOCK_IMAGE_TILE_GRID_CLASSES: Record<
  StockImageAspectRatio,
  string
> = {
  square: 'tw-grid-cols-2 min-[620px]:tw-grid-cols-3 tablet:tw-grid-cols-4',
  landscape: 'tw-grid-cols-2 min-[620px]:tw-grid-cols-3 tablet:tw-grid-cols-4',
  cover: 'tw-grid-cols-1 min-[620px]:tw-grid-cols-2',
  original: 'tw-grid-cols-2 min-[620px]:tw-grid-cols-3 tablet:tw-grid-cols-4',
};
